import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LabeledImage } from '../../../components/LabeledImage';
import { faDownload, faShare } from '@fortawesome/free-solid-svg-icons';
import { HomeSectionLayout } from '../../../components/HomeSectionLayout';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { useState, useEffect } from 'react';
import RichMarkdownContent from '../../Announcements/RichMarkdownContent';
import { SECTION_COLORS } from '../../../utils/colors';

interface LabeledImageData {
  title: string;
  description: string;
  image_url: string;
  labels: {
    text: string;
    coordinates: { x: number; y: number }[];
  }[];
}

interface LabeledImagesSectionProps {
  labeledImages?: LabeledImageData[];
}

function LabeledImageItem({ item, index }: { item: LabeledImageData, index: number }) {
  const { screenLargerThan } = useScreenSize();
  const isLarge = screenLargerThan('lg');
  const isSmall = screenLargerThan('sm');
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      setViewportHeight(window.innerHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const imageAspectRatio = 0.72;
  const mobileImageWidth = Math.floor(viewportHeight * 0.6 * imageAspectRatio);
  const imageWidth = isLarge ? 350 : isSmall ? 300 : mobileImageWidth || 280;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = item.image_url;
    link.download = `${item.title || 'image'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    try {
      const response = await fetch(item.image_url);
      const blob = await response.blob();
      const file = new File([blob], `${item.title || 'image'}.png`, { type: 'image/png' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: item.title,
          text: item.description ? item.description.substring(0, 100) : undefined,
        });
      } else {
        alert('Sharing is not supported on this browser. Please use the download button instead.');
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: isLarge ? 'row' : 'column',
      gap: '20px',
      alignItems: isLarge ? 'flex-start' : 'center',
      width: '100%',
    }}>
      <div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '5px'
        }}>
          <div style={{
            minWidth: `${imageWidth}px`,
            maxWidth: `${imageWidth}px`,
            border: '1px solid var(--color-base-300)',
          }}>
            <LabeledImage
              src={item.image_url}
              labels={item.labels}
            />
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: '5px'
          }}>
            <button className="btn btn-primary btn-sm" onClick={handleDownload}>
              <FontAwesomeIcon icon={faDownload} />
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleShare}>
              <FontAwesomeIcon icon={faShare} />
            </button>
          </div>
        </div>
      </div>
      {item.description && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px',
          flex: 1,
        }}>
          <div style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            textAlign: 'justify',
          }}>
            <RichMarkdownContent>
              {item.description}
            </RichMarkdownContent>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LabeledImagesSection({ labeledImages }: LabeledImagesSectionProps) {
  if (!labeledImages || labeledImages.length === 0) {
    return null;
  }

  // Filter out items without images
  const validItems = labeledImages.filter(item => item.image_url);

  if (validItems.length === 0) {
    return null;
  }

  return (
    <>
      {validItems.map((item, index) => (
        <HomeSectionLayout.Container
          key={`labeled-image-${index}`}
          style={{
            backgroundColor: SECTION_COLORS[index % SECTION_COLORS.length].bg,
            color: SECTION_COLORS[index % SECTION_COLORS.length].text,
          }}>
          {item.title && (
            <HomeSectionLayout.Header>{item.title}</HomeSectionLayout.Header>
          )}
          <HomeSectionLayout.Content>
            <LabeledImageItem
              item={item}
              index={index}
            />
          </HomeSectionLayout.Content>
        </HomeSectionLayout.Container>
      ))}
    </>
  );
}
